import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Check, ScanLine, ArrowRight, MapPin } from "lucide-react";
import api from "../lib/api";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { Reveal, Overline } from "../components/Reveal";
import { SERVICES, PROCESS, IMAGES, SERVICE_AREAS } from "../data/content";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";
import { Cm3Badge } from "../components/Cm3Badge";

const FAQS = [
  { q: "How long does a court resurface take?", a: "Most projects are completed within several days depending on size, condition and weather. We confirm a schedule after inspection." },
  { q: "Can you match custom colours and add a logo?", a: "Yes — custom colours, centre logos, crests and graphics are a core part of what Speedy Resurfacing offers." },
  { q: "Do you service my area?", a: "We serve Sydney and surrounding NSW regions. Enter your postcode in the Court Scan or quote form and we'll confirm." },
  { q: "How long will the finished court last?", a: "With quality coatings and light maintenance, a court we renew holds its colour and grip for years. We advise on care at handover." },
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const service = SERVICES.find((s) => s.slug === slug);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, [slug]);

  if (!service) return (
    <div className="pt-40 pb-24 text-center">
      <h1 className="font-heading font-black text-4xl">Service not found</h1>
      <Link to="/services" className="mt-6 inline-block text-neon">← All services</Link>
    </div>
  );

  const related = projects.filter((p) => p.category === service.category);
  const feat = related[0] || projects[0];

  return (
    <div className="min-h-screen noise">
      <Seo title={`${service.title} Sydney & NSW | Speedy Resurfacing`}
           description={`${service.intro} Servicing ${SERVICE_AREAS.slice(0, 5).join(", ")} and greater NSW. Free quotes.`}
           path={`/services/${service.slug}`}
           jsonLd={{
             "@context": "https://schema.org",
             "@graph": [
               { "@type": "Service", "name": service.title, "description": service.intro, "serviceType": service.title,
                 "provider": { "@type": "LocalBusiness", "name": "Speedy Resurfacing", "telephone": "0466 495 495",
                               "url": "https://www.speedyresurfacing.com.au" },
                 "areaServed": SERVICE_AREAS },
               { "@type": "FAQPage", "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q,
                 "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
             ],
           }} />
      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <img {...responsive(IMAGES.nightTennis, "100vw", [640, 1000, 1600])} decoding="async" alt={service.title} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-obsidian/50" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <nav className="flex items-center gap-2 font-mono text-xs text-zinc-500 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-neon">Home</Link><span>/</span>
            <Link to="/services" className="hover:text-neon">Services</Link><span>/</span>
            <span className="text-zinc-300">{service.title}</span>
          </nav>
          <Overline>Court Resurfacing · Sydney</Overline>
          <h1 className="mt-5 font-heading font-black text-5xl md:text-7xl tracking-tighter max-w-3xl">{service.title}</h1>
          <p className="mt-5 max-w-xl text-lg text-zinc-300">{service.intro}</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 md:px-10 pb-24">
        {/* Problem / solution */}
        <div className="grid md:grid-cols-2 gap-6">
          <Reveal><div className="rounded-lg border border-white/10 bg-obsidian-800 p-8 h-full">
            <span className="font-mono text-xs text-zinc-500 uppercase tracking-widest">The Problem</span>
            <p className="mt-4 text-zinc-300">{service.problem}</p>
          </div></Reveal>
          <Reveal delay={0.1}><div className="rounded-lg border border-neon/20 bg-obsidian-800 p-8 h-full">
            <span className="font-mono text-xs text-neon uppercase tracking-widest">The Speedy Solution</span>
            <p className="mt-4 text-zinc-300">{service.solution}</p>
          </div></Reveal>
        </div>

        {/* Before/after */}
        {feat && (
          <div className="mt-16">
            <Reveal><h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-6">A Real Transformation</h2></Reveal>
            <Reveal delay={0.1}><BeforeAfterSlider before={feat.before_image || IMAGES.worn} after={feat.after_image || IMAGES.speedyBball} testid="service-slider" /></Reveal>
          </div>
        )}

        {/* Process */}
        <div className="mt-16">
          <Reveal><h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-8">Our Process</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={(i % 3) * 0.05}><div className="rounded-lg border border-white/10 bg-obsidian-800 p-6">
                <span className="font-heading font-black text-2xl text-zinc-700">{p.n}</span>
                <h3 className="mt-2 font-heading font-bold text-lg">{p.title}</h3>
                <p className="mt-1 text-sm text-zinc-400">{p.text}</p>
              </div></Reveal>
            ))}
          </div>
        </div>

        {/* Customisation / materials */}
        <div className="mt-16 grid md:grid-cols-2 gap-6">
          <Reveal><div className="rounded-lg border border-white/10 bg-obsidian-800 p-8">
            <h3 className="font-heading font-bold text-xl mb-4">Materials &amp; Coatings</h3>
            <ul className="space-y-2.5">
              {["Layered acrylic resurfacing systems", "Crack repair & substrate preparation", "High-grip, weather-durable coatings", "Australian-standard court paints"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-zinc-300"><Check size={16} className="text-neon mt-0.5" /> {t}</li>
              ))}
            </ul>
          </div></Reveal>
          <Reveal delay={0.1}><div className="rounded-lg border border-white/10 bg-obsidian-800 p-8">
            <h3 className="font-heading font-bold text-xl mb-4">Customisation Options</h3>
            <ul className="space-y-2.5">
              {["Custom base, key, border & line colours", "Centre logos, crests & graphics", "Single or multi-sport layouts", "Branding for schools & businesses"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-zinc-300"><Check size={16} className="text-gold mt-0.5" /> {t}</li>
              ))}
            </ul>
          </div></Reveal>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <Reveal><h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-6">FAQ</h2></Reveal>
          <Accordion type="single" collapsible className="rounded-lg border border-white/10 bg-obsidian-800 px-6" data-testid="service-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                <AccordionTrigger className="text-left font-heading font-semibold hover:text-neon">{f.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Service areas */}
        <div className="mt-16">
          <Reveal><h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-4 flex items-center gap-2"><MapPin className="text-neon" size={22} /> Service Areas</h2></Reveal>
          <div className="flex flex-wrap gap-2">
            {SERVICE_AREAS.map((a) => (
              <span key={a} className="rounded-sm border border-white/10 px-3 py-1.5 text-sm text-zinc-400">{a}</span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 flex flex-wrap items-center gap-4" data-testid="service-compliance">
          <Cm3Badge />
          <p className="text-sm text-zinc-400">Independently verified WHS &amp; insurances — procurement-ready for schools, councils and clubs.</p>
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <button onClick={() => nav("/quote")} data-testid="service-quote-cta" className="flex items-center justify-between rounded-lg bg-neon p-8 text-obsidian shadow-neon group">
            <span className="font-heading font-black text-2xl">Request a Quote</span>
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={28} />
          </button>
          <button onClick={() => nav("/court-scan")} className="flex items-center justify-between rounded-lg border border-neon/30 bg-obsidian-800 p-8 text-white hover:border-neon transition-colors group">
            <span className="font-heading font-black text-2xl">Try Court Scan™</span>
            <ScanLine className="text-neon" size={28} />
          </button>
        </div>
      </div>
    </div>
  );
}
